import React from "react";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHeading, PillButton } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";

const SERVICES = [
  {
    icon: "LineChart",
    title: "Trading Evaluation Software Solutions",
    intro:
      "Vardhix develops software that allows businesses to operate simulated trading evaluation programs.",
    items: [
      "User registration and onboarding", "Trading account allocation", "Simulated trading terminals",
      "Futures and options simulation", "Rule-based account evaluation", "Profit and loss calculation",
      "Drawdown monitoring", "Consistency-rule monitoring", "Stop-loss compliance systems",
      "Lot-size controls", "Account breach detection", "Performance analytics",
      "Administrative dashboards", "Payout eligibility workflows", "KYC integration", "Support and ticketing systems",
    ],
  },
  {
    icon: "CircuitBoard",
    title: "Fintech Platform Development",
    intro: "Custom fintech systems engineered for reliability, scale and security.",
    items: [
      "Custom fintech software", "Digital dashboards", "Transaction workflow systems",
      "User account management", "Analytics platforms", "Financial reporting tools",
      "Risk engines", "Data monitoring", "API integrations",
    ],
  },
  {
    icon: "Workflow",
    title: "Business Automation",
    intro: "Automating operations to reduce manual effort and improve consistency.",
    items: [
      "Automated onboarding", "Notification systems", "Workflow management",
      "Customer support automation", "Account monitoring", "Rule enforcement",
      "Reporting automation", "Administrative controls",
    ],
  },
  {
    icon: "Layers",
    title: "Platform Operations",
    intro: "We manage the full operational lifecycle of our platforms.",
    items: [
      "Product operations", "Technology infrastructure", "Customer experience", "Risk systems",
      "Platform monitoring", "Brand development", "Growth strategy", "Vendor coordination",
      "Data systems", "Internal reporting",
    ],
  },
  {
    icon: "Code2",
    title: "Custom Software Development",
    intro: "End-to-end product engineering across web, cloud and data.",
    items: [
      "Web applications", "Mobile-responsive platforms", "SaaS products", "Internal management tools",
      "Admin panels", "Real-time dashboards", "Cloud systems", "Database architecture",
      "API development", "Third-party integrations",
    ],
  },
];

export default function WhatWeDo() {
  useSeo({
    title: "What We Do",
    description:
      "Vardhix develops trading evaluation software, fintech platforms, business automation, platform operations and custom software for the Indian market.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "What We Do", path: "/what-we-do" }],
  });

  return (
    <>
      <PageHero
        overline="What We Do"
        title="Software, infrastructure and automation across the fintech stack"
        sub="From trading evaluation technology to custom software and platform operations, we build and run the systems that power modern digital businesses."
      />

      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 space-y-6">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.title} delay={0.04}>
              <div className="glass rounded-3xl p-8 md:p-10" data-testid={`wwd-service-${idx}`}>
                <div className="grid lg:grid-cols-12 gap-8">
                  <div className="lg:col-span-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                      <Icon name={s.icon} className="h-6 w-6" />
                    </span>
                    <h2 className="mt-6 text-2xl md:text-3xl font-heading font-semibold text-white leading-tight">{s.title}</h2>
                    <p className="mt-4 text-slate-400 leading-relaxed">{s.intro}</p>
                  </div>
                  <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 content-start">
                    {s.items.map((it) => (
                      <div key={it} className="flex items-start gap-2.5 py-1.5 border-b border-white/5">
                        <Icon name="Check" className="h-4 w-4 text-teal shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-200">{it}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-20 border-t border-white/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <SectionHeading center title="Have a technology challenge in mind?" />
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <PillButton to="/contact" testid="wwd-contact-cta">Talk to Our Team</PillButton>
            <PillButton to="/technologies" variant="secondary" testid="wwd-tech-cta">See Our Technologies</PillButton>
          </div>
        </div>
      </section>
    </>
  );
}
